/**
 * Dungeon Generation Module - Pure functions for dungeon creation
 *
 * Procedurally generates dungeon hex maps with enemies, loot, and traps.
 * This module contains logic, not data - part of the modular architecture.
 */

import {
  DUNGEON_CONFIG,
  TILE_TYPES,
  ENEMY_TEMPLATES,
  EQUIPMENT_STATS,
  EQUIPMENT_ICONS,
} from '../../data/dungeon';
import { coordsToId, getAdjacentCoords } from '../../utils/hexGrid';

/**
 * Generate a procedural dungeon floor
 * @param {number} floor - Floor number (1-indexed)
 * @param {string} tierId - Dungeon tier ID
 * @param {Array<string>} mobPool - Array of normal mob IDs for this dungeon
 * @param {Array<string>} bossPool - Array of boss mob IDs for this dungeon
 * @param {number} seed - Random seed for reproducibility
 * @param {Object} options - Optional tower configuration
 * @param {boolean} options.isTower - Whether this is a tower floor
 * @param {number} options.maxFloors - Total floors in the tower
 * @returns {Object} Dungeon data with tiles, enemies, etc.
 */
export function generateDungeon(floor, tierId, mobPool = [], bossPool = [], seed = Date.now(), options = {}) {
  const { isTower = false, maxFloors = 1 } = options;
  const rng = seededRandom(seed);
  const size = DUNGEON_CONFIG.GRID_SIZE;
  const halfSize = Math.floor(size / 2);

  // Initialize tiles
  const tiles = {};
  const enemies = {};

  // Place entrance at center
  const entranceCoords = { q: 0, r: 0 };
  const entranceId = coordsToId(0, 0);
  tiles[entranceId] = {
    q: 0,
    r: 0,
    type: TILE_TYPES.ENTRANCE,
    icon: '🚪',
    discovered: true, // Entrance starts discovered
    visited: false,
  };

  // Generate all hex tiles in range
  const allCoords = [];
  for (let q = -halfSize; q <= halfSize; q++) {
    for (let r = -halfSize; r <= halfSize; r++) {
      // Skip if outside hex bounds
      const s = -q - r;
      if (Math.abs(s) > halfSize) continue;

      // Skip entrance
      if (q === 0 && r === 0) continue;

      allCoords.push({ q, r });
    }
  }

  // Shuffle coordinates for random placement
  shuffle(allCoords, rng);

  // Find furthest tile from entrance (for boss/stairs)
  let furthestCoords = null;
  let maxDist = 0;
  for (const coords of allCoords) {
    const dist = hexDistance(coords.q, coords.r, 0, 0);
    if (dist > maxDist) {
      maxDist = dist;
      furthestCoords = coords;
    }
  }

  const furthestId = coordsToId(furthestCoords.q, furthestCoords.r);

  // Determine if this is the final floor (boss floor) or not
  const isFinalFloor = !isTower || floor >= maxFloors;

  if (isFinalFloor) {
    // Place boss tile on final floor (or regular dungeons)
    tiles[furthestId] = {
      q: furthestCoords.q,
      r: furthestCoords.r,
      type: TILE_TYPES.BOSS,
      icon: '👑',
      discovered: false,
      visited: false,
    };
    // Place boss enemy from provided pool
    enemies[furthestId] = generateEnemy('boss', floor, tierId, rng, mobPool, bossPool);
  } else {
    // Place stairs UP on non-final tower floors (to ascend to next floor)
    tiles[furthestId] = {
      q: furthestCoords.q,
      r: furthestCoords.r,
      type: TILE_TYPES.STAIRS_UP,
      icon: '⬆️',
      discovered: false,
      visited: false,
    };
  }

  // Remove furthest coords from pool
  let remainingCoords = allCoords.filter(c =>
    !(c.q === furthestCoords.q && c.r === furthestCoords.r)
  );

  // Tower-specific: Place stairs DOWN near entrance (except floor 1) to descend to previous floor
  let stairsDownId = null;
  if (isTower && floor > 1) {
    const stairsDownCoords = findNearbyEmptyTile(entranceCoords, remainingCoords, rng, 2);
    if (stairsDownCoords) {
      stairsDownId = coordsToId(stairsDownCoords.q, stairsDownCoords.r);
      tiles[stairsDownId] = {
        q: stairsDownCoords.q,
        r: stairsDownCoords.r,
        type: TILE_TYPES.STAIRS_DOWN,
        icon: '⬇️',
        discovered: false,
        visited: false,
      };
      remainingCoords = remainingCoords.filter(c =>
        !(c.q === stairsDownCoords.q && c.r === stairsDownCoords.r)
      );
    }
  }

  // Tower-specific: Place checkpoint at mid-distance
  let checkpointId = null;
  if (isTower) {
    const checkpointCoords = findMidDistanceTile(entranceCoords, remainingCoords, rng, maxDist);
    if (checkpointCoords) {
      checkpointId = coordsToId(checkpointCoords.q, checkpointCoords.r);
      tiles[checkpointId] = {
        q: checkpointCoords.q,
        r: checkpointCoords.r,
        type: TILE_TYPES.CHECKPOINT,
        icon: '🏳️',
        discovered: false,
        visited: false,
      };
      remainingCoords = remainingCoords.filter(c =>
        !(c.q === checkpointCoords.q && c.r === checkpointCoords.r)
      );
    }
  }

  // Place other tiles randomly based on weights (towers use harder weights)
  for (const coords of remainingCoords) {
    const tileType = selectTileType(rng, isTower);
    const tileId = coordsToId(coords.q, coords.r);

    tiles[tileId] = {
      q: coords.q,
      r: coords.r,
      type: tileType,
      icon: getTileIcon(tileType),
      discovered: false,
      visited: false,
    };

    // Place enemies on combat tiles from provided pool
    if (tileType === TILE_TYPES.COMBAT) {
      enemies[tileId] = generateEnemy('normal', floor, tierId, rng, mobPool, bossPool);
    }
  }

  return {
    floor,
    tierId,
    seed,
    tiles,
    enemies,
    playerPosition: entranceId,
    completed: false,
    bossDefeated: false,
    // Tower-specific fields
    isTower,
    maxFloors: isTower ? maxFloors : 1,
    stairsUpId: isFinalFloor ? null : furthestId,  // Stairs UP at furthest point (to ascend)
    stairsDownId,  // Stairs DOWN near entrance (to descend, only on floors 2+)
    checkpointId,
  };
}

/**
 * Find an empty tile near a target position
 * @param {Object} targetCoords - Target {q, r} coordinates
 * @param {Array} availableCoords - Pool of available coordinates
 * @param {Function} rng - Seeded random function
 * @param {number} maxDistance - Maximum distance from target
 * @returns {Object|null} Found coordinates or null
 */
function findNearbyEmptyTile(targetCoords, availableCoords, rng, maxDistance = 2) {
  const nearby = availableCoords.filter(c => {
    const dist = hexDistance(c.q, c.r, targetCoords.q, targetCoords.r);
    return dist > 0 && dist <= maxDistance;
  });

  if (nearby.length === 0) return null;
  return nearby[Math.floor(rng() * nearby.length)];
}

/**
 * Find a tile at approximately mid-distance from entrance
 * @param {Object} entranceCoords - Entrance {q, r} coordinates
 * @param {Array} availableCoords - Pool of available coordinates
 * @param {Function} rng - Seeded random function
 * @param {number} maxDist - Maximum distance in the dungeon
 * @returns {Object|null} Found coordinates or null
 */
function findMidDistanceTile(entranceCoords, availableCoords, rng, maxDist) {
  const targetDist = maxDist * 0.5; // ~50% of max distance
  const tolerance = 1; // Allow +/- 1 tile from target

  const candidates = availableCoords.filter(c => {
    const dist = hexDistance(c.q, c.r, entranceCoords.q, entranceCoords.r);
    return Math.abs(dist - targetDist) <= tolerance;
  });

  if (candidates.length === 0) {
    // Fallback: just pick something in the middle range
    const sorted = [...availableCoords].sort((a, b) => {
      const distA = hexDistance(a.q, a.r, entranceCoords.q, entranceCoords.r);
      const distB = hexDistance(b.q, b.r, entranceCoords.q, entranceCoords.r);
      return distA - distB;
    });
    const midIndex = Math.floor(sorted.length / 2);
    return sorted[midIndex] || null;
  }

  return candidates[Math.floor(rng() * candidates.length)];
}

/**
 * Select a random tile type based on weights
 * @param {Function} rng - Seeded random function
 * @param {boolean} isTower - Whether this is a tower (uses harder tile weights)
 * @returns {string} Tile type
 */
function selectTileType(rng, isTower = false) {
  // Use tower weights for harder content (more combat, less healing)
  const weights = isTower ? DUNGEON_CONFIG.TOWER_TILE_WEIGHTS : DUNGEON_CONFIG.TILE_WEIGHTS;
  const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
  const roll = rng() * totalWeight;

  let cumulative = 0;
  for (const [type, weight] of Object.entries(weights)) {
    cumulative += weight;
    if (roll < cumulative) return type;
  }

  return TILE_TYPES.EMPTY;
}

/**
 * Get icon for tile type
 * @param {string} type - Tile type
 * @returns {string} Emoji icon
 */
function getTileIcon(type) {
  const icons = {
    [TILE_TYPES.EMPTY]: '·',
    [TILE_TYPES.COMBAT]: '⚔️',
    [TILE_TYPES.TREASURE]: '💎',
    [TILE_TYPES.TRAP]: '💀',
    [TILE_TYPES.REST]: '🔥',
    [TILE_TYPES.BOSS]: '👑',
    [TILE_TYPES.ENTRANCE]: '🚪',
    // Tower-specific tiles
    [TILE_TYPES.STAIRS_DOWN]: '⬇️',
    [TILE_TYPES.STAIRS_UP]: '⬆️',
    [TILE_TYPES.CHECKPOINT]: '🏳️',
  };
  return icons[type] || '·';
}

/**
 * Get tier scaling based on dungeon's baseEnemyLevel
 *
 * BALANCE NOTE: Mobs have base stats designed for their tier's LOWEST level dungeon.
 * This scaling provides a multiplier for higher-level dungeons within the same tier.
 *
 * AGGRESSIVE EXPONENTIAL SCALING - enemies get significantly harder at higher levels
 * Base difficulty multiplier of 2.0x applied to all enemies (dungeons should be challenging)
 *
 * Tier 1 (Lv 5-20): Base mobs designed for Lv 5
 *   - Lv 5: 2.0x, Lv 10: 2.5x, Lv 15: 3.1x, Lv 20: 3.9x
 * Tier 2 (Lv 25-40): Base mobs designed for Lv 25
 *   - Lv 25: 2.0x, Lv 30: 2.5x, Lv 35: 3.1x, Lv 40: 3.9x
 * And so on for Tiers 3-5
 *
 * @param {string} tierId - Dungeon tier ID (e.g., 'tier1_lv5', 'tier2_lv30', or legacy 'tier1')
 * @returns {number} Scaling multiplier for enemy stats
 */
function getTierScaling(tierId) {
  // Base difficulty multiplier - dungeons should be challenging!
  const BASE_DIFFICULTY = 2.0;

  // Find the dungeon config by ID
  const dungeonConfig = DUNGEON_CONFIG.TIERS.find(d => d.id === tierId);

  if (dungeonConfig && dungeonConfig.baseEnemyLevel) {
    const level = dungeonConfig.baseEnemyLevel;
    const tier = dungeonConfig.tier;

    // Get the base level for this tier (lowest level dungeon in the tier)
    const tierBaseLevels = { 1: 5, 2: 25, 3: 45, 4: 65, 5: 85 };
    const baseLevel = tierBaseLevels[tier] || 5;

    // EXPONENTIAL scaling: 1.25^(levelsAboveBase/5)
    // Each 5 levels multiplies stats by 1.25x (compounds aggressively)
    // Lv 5: 1.0x, Lv 10: 1.25x, Lv 15: 1.56x, Lv 20: 1.95x
    const levelsAboveBase = level - baseLevel;
    const scalingSteps = levelsAboveBase / 5;
    return BASE_DIFFICULTY * Math.pow(1.25, scalingSteps);
  }

  // Legacy fallback for old tier IDs (tier1, tier2, etc.)
  // These assume the highest level dungeon of each tier (3 steps above base = 1.95x)
  const baseTier = tierId.split('_')[0];
  const legacyScaling = {
    tier1: BASE_DIFFICULTY * 1.95,  // Lv 20 equivalent (1.25^3)
    tier2: BASE_DIFFICULTY * 1.95,  // Lv 40 equivalent
    tier3: BASE_DIFFICULTY * 1.95,  // Lv 60 equivalent
    tier4: BASE_DIFFICULTY * 1.95,  // Lv 80 equivalent
    tier5: BASE_DIFFICULTY * 1.95,  // Lv 100 equivalent
  };
  return legacyScaling[baseTier] || BASE_DIFFICULTY;
}

/**
 * Get enemy level for a dungeon
 * @param {string} tierId - Dungeon tier ID
 * @returns {number} Enemy level
 */
function getDungeonEnemyLevel(tierId) {
  const dungeonConfig = DUNGEON_CONFIG.TIERS.find(d => d.id === tierId);
  if (dungeonConfig && dungeonConfig.baseEnemyLevel) {
    return dungeonConfig.baseEnemyLevel;
  }

  // Legacy fallback
  const baseTier = tierId.split('_')[0];
  const legacyLevels = { tier1: 5, tier2: 15, tier3: 30, tier4: 50, tier5: 75 };
  return legacyLevels[baseTier] || 5;
}

/**
 * Generate an enemy for a tile
 * @param {string} type - 'normal' or 'boss'
 * @param {number} floor - Dungeon floor
 * @param {string} tierId - Dungeon tier
 * @param {Function} rng - Seeded random function
 * @param {Array<string>} mobPool - Normal mob IDs
 * @param {Array<string>} bossPool - Boss mob IDs
 * @returns {Object} Enemy data
 */
function generateEnemy(type, floor, tierId, rng, mobPool = [], bossPool = []) {
  // Select enemy template from provided pools
  let template;
  if (type === 'boss') {
    // Use boss pool if provided, otherwise fall back to all bosses
    if (bossPool.length > 0) {
      const bossId = bossPool[Math.floor(rng() * bossPool.length)];
      template = ENEMY_TEMPLATES[bossId];
      // If boss ID didn't resolve, try fallback
      if (!template) {
        console.warn(`Boss template not found for ID: ${bossId}, falling back to all bosses`);
        const bosses = Object.values(ENEMY_TEMPLATES).filter(e => e.isBoss);
        template = bosses[Math.floor(rng() * bosses.length)];
      }
    } else {
      const bosses = Object.values(ENEMY_TEMPLATES).filter(e => e.isBoss);
      template = bosses[Math.floor(rng() * bosses.length)];
    }
    // Final fallback: ensure we always have a boss
    if (!template) {
      console.warn('No boss found in templates, using hardcoded fallback');
      template = ENEMY_TEMPLATES.boss_ogre || {
        id: 'boss_ogre',
        name: 'Ogre Chieftain',
        icon: '👿',
        isBoss: true,
        baseStats: { health: 200, attack: 20, defense: 10, speed: 2, range: 1 },
        lootTable: 'boss',
        drops: [],
      };
    }
  } else {
    // Use mob pool if provided, otherwise fall back to all normals
    if (mobPool.length > 0) {
      const mobId = mobPool[Math.floor(rng() * mobPool.length)];
      template = ENEMY_TEMPLATES[mobId];
    } else {
      const normals = Object.values(ENEMY_TEMPLATES).filter(e => !e.isBoss);
      template = normals[Math.floor(rng() * normals.length)];
    }
  }

  // Safety check for normal mobs
  if (!template) {
    console.error(`Failed to generate enemy - type: ${type}, mobPool:`, mobPool, 'bossPool:', bossPool);
    // Fallback to a basic enemy
    template = ENEMY_TEMPLATES.goblin || Object.values(ENEMY_TEMPLATES)[0];
  }

  // Apply tier and floor scaling (both exponential)
  const tierMultiplier = getTierScaling(tierId);
  // Floor scaling: 1.20^(floor-1) - aggressive exponential per floor
  // Floor 1: 1.0x, Floor 2: 1.20x, Floor 3: 1.44x, Floor 5: 2.07x, Floor 10: 5.16x
  const floorMultiplier = Math.pow(1.20, floor - 1);

  // Base stat multipliers (applied on top of everything else)
  // These boost the raw mob stats to make dungeons more challenging
  const STAT_MULTIPLIERS = {
    health: 1.5,   // +50% HP
    attack: 1.4,   // +40% attack
    defense: 1.3,  // +30% defense
  };

  const finalHealthMult = tierMultiplier * floorMultiplier * STAT_MULTIPLIERS.health;
  const finalAttackMult = tierMultiplier * floorMultiplier * STAT_MULTIPLIERS.attack;
  const finalDefenseMult = tierMultiplier * floorMultiplier * STAT_MULTIPLIERS.defense;

  return {
    ...template,
    stats: {
      health: Math.floor(template.baseStats.health * finalHealthMult),
      maxHealth: Math.floor(template.baseStats.health * finalHealthMult),
      attack: Math.floor(template.baseStats.attack * finalAttackMult),
      defense: Math.floor(template.baseStats.defense * finalDefenseMult),
      speed: template.baseStats.speed,
      range: template.baseStats.range,
    },
    level: getDungeonEnemyLevel(tierId),
  };
}

/**
 * Generate a random equipment drop
 * @param {string} slot - Equipment slot (weapon, armor, etc.)
 * @param {string} rarity - Rarity tier (common, uncommon, rare, epic, legendary)
 * @param {number} seed - Random seed
 * @returns {Object|null} Equipment item or null if invalid
 */
export function generateEquipment(slot, rarity, seed = Date.now()) {
  const rng = seededRandom(seed);

  const slotData = EQUIPMENT_STATS[slot];
  if (!slotData) return null;

  const rarityData = slotData[rarity];
  if (!rarityData) return null;

  // Generate stats
  const stats = {};
  for (const [statName, range] of Object.entries(rarityData)) {
    if (Array.isArray(range) && range.length === 2) {
      const [min, max] = range;
      stats[statName] = min + rng() * (max - min);

      // Round based on stat type
      if (statName === 'health' || statName === 'attack' || statName === 'defense' || statName === 'speed') {
        stats[statName] = Math.floor(stats[statName]);
      } else {
        stats[statName] = parseFloat(stats[statName].toFixed(3));
      }
    }
  }

  // Select icon
  const icons = EQUIPMENT_ICONS[slot]?.[rarity] || ['�'];
  const icon = icons[Math.floor(rng() * icons.length)];

  // Generate name
  const name = generateEquipmentName(slot, rarity, rng);

  return {
    id: `${slot}_${rarity}_${Date.now()}_${Math.floor(rng() * 10000)}`,
    slot,
    name,
    icon,
    rarity,
    stats,
  };
}

/**
 * Generate a procedural equipment name
 * @param {string} slot - Equipment slot
 * @param {string} rarity - Rarity tier
 * @param {Function} rng - Seeded random function
 * @returns {string} Generated name
 */
function generateEquipmentName(slot, rarity, rng) {
  const prefixes = {
    common: ['Worn', 'Rusty', 'Old', 'Simple', 'Basic'],
    uncommon: ['Fine', 'Sturdy', 'Sharp', 'Reinforced', 'Quality'],
    rare: ['Enchanted', 'Masterwork', 'Ancient', 'Blessed', 'Forged'],
    epic: ['Legendary', 'Mystic', 'Dragon', 'Celestial', 'Infernal'],
    legendary: ['Godly', 'Divine', 'Eternal', 'Cosmic', 'Primordial'],
  };

  const slotNames = {
    weapon: ['Sword', 'Blade', 'Axe', 'Mace', 'Spear'],
    armor: ['Armor', 'Plate', 'Mail', 'Cuirass', 'Breastplate'],
    helmet: ['Helmet', 'Helm', 'Crown', 'Cap', 'Circlet'],
    boots: ['Boots', 'Greaves', 'Treads', 'Sabatons', 'Shoes'],
    accessory: ['Ring', 'Amulet', 'Pendant', 'Talisman', 'Charm'],
  };

  const prefix = prefixes[rarity][Math.floor(rng() * prefixes[rarity].length)];
  const baseName = slotNames[slot][Math.floor(rng() * slotNames[slot].length)];

  return `${prefix} ${baseName}`;
}

/**
 * Seeded random number generator (LCG)
 * @param {number} seed - Initial seed
 * @returns {Function} Random function returning 0-1
 */
function seededRandom(seed) {
  let state = seed;
  return function() {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

/**
 * Fisher-Yates shuffle with seeded RNG
 * @param {Array} array - Array to shuffle (mutates in place)
 * @param {Function} rng - Seeded random function
 * @returns {Array} Shuffled array
 */
function shuffle(array, rng) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

/**
 * Calculate hex distance between two coordinates
 * @param {number} q1 - First Q coordinate
 * @param {number} r1 - First R coordinate
 * @param {number} q2 - Second Q coordinate
 * @param {number} r2 - Second R coordinate
 * @returns {number} Distance in hex steps
 */
function hexDistance(q1, r1, q2, r2) {
  return (Math.abs(q1 - q2) + Math.abs(r1 - r2) + Math.abs((q1 + r1) - (q2 + r2))) / 2;
}

/**
 * Reveal tiles within vision range of player position
 * @param {Object} tiles - Current tiles object
 * @param {string} playerPos - Player position tile ID
 * @param {number} visionRange - Vision range in tiles (default 1)
 * @returns {Object} Updated tiles with discovered flags
 */
export function revealTiles(tiles, playerPos, visionRange = 1) {
  const { q, r } = tiles[playerPos];
  const revealed = { ...tiles };

  // Reveal player tile
  revealed[playerPos] = { ...revealed[playerPos], discovered: true };

  // Reveal adjacent tiles within range
  for (let dq = -visionRange; dq <= visionRange; dq++) {
    for (let dr = -visionRange; dr <= visionRange; dr++) {
      const ds = -dq - dr;
      if (Math.abs(ds) > visionRange) continue;

      const tileQ = q + dq;
      const tileR = r + dr;
      const tileId = coordsToId(tileQ, tileR);

      if (revealed[tileId]) {
        revealed[tileId] = { ...revealed[tileId], discovered: true };
      }
    }
  }

  return revealed;
}

// ============================================================================
// ENCOUNTER GENERATION (Smaller, no-loot combat areas)
// ============================================================================

/**
 * Encounter tile types (simpler than dungeons)
 */
export const ENCOUNTER_TILE_TYPES = {
  EMPTY: 'empty',
  COMBAT: 'combat',
  REST: 'rest',
  ENTRANCE: 'entrance',
  EXIT: 'exit',  // No boss, just an exit
};

/**
 * Generate a procedural encounter (smaller dungeon, no loot)
 * @param {Array<string>} mobPool - Array of mob IDs for this encounter
 * @param {number} playerLevel - Player's current level (for scaling)
 * @param {number} seed - Random seed for reproducibility
 * @returns {Object} Encounter data with tiles, enemies, etc.
 */
export function generateEncounter(mobPool = [], playerLevel = 1, seed = Date.now()) {
  const rng = seededRandom(seed);
  const size = 5; // Smaller than dungeons (5x5 vs 7x7)
  const halfSize = Math.floor(size / 2);

  // Initialize tiles
  const tiles = {};
  const enemies = {};

  // Encounter tile weights (only combat and rest, no treasure/traps)
  // Less healing than dungeons - just a small campfire here and there
  const tileWeights = {
    empty: 25,
    combat: 65,
    rest: 10,  // Much less healing than dungeons
  };

  // Place entrance at center
  const entranceId = coordsToId(0, 0);
  tiles[entranceId] = {
    q: 0,
    r: 0,
    type: ENCOUNTER_TILE_TYPES.ENTRANCE,
    icon: '🚪',
    discovered: true,
    visited: false,
  };

  // Generate all hex tiles in range
  const allCoords = [];
  for (let q = -halfSize; q <= halfSize; q++) {
    for (let r = -halfSize; r <= halfSize; r++) {
      const s = -q - r;
      if (Math.abs(s) > halfSize) continue;
      if (q === 0 && r === 0) continue; // Skip entrance
      allCoords.push({ q, r });
    }
  }

  // Shuffle coordinates for random placement
  shuffle(allCoords, rng);

  // Place exit tile (furthest from entrance)
  let exitCoords = null;
  let maxDist = 0;
  for (const coords of allCoords) {
    const dist = hexDistance(coords.q, coords.r, 0, 0);
    if (dist > maxDist) {
      maxDist = dist;
      exitCoords = coords;
    }
  }

  const exitId = coordsToId(exitCoords.q, exitCoords.r);
  tiles[exitId] = {
    q: exitCoords.q,
    r: exitCoords.r,
    type: ENCOUNTER_TILE_TYPES.EXIT,
    icon: '🚪',
    discovered: false,
    visited: false,
  };

  // Remove exit coords from pool
  const remainingCoords = allCoords.filter(c =>
    !(c.q === exitCoords.q && c.r === exitCoords.r)
  );

  // Place other tiles randomly based on weights
  for (const coords of remainingCoords) {
    const tileType = selectEncounterTileType(rng, tileWeights);
    const tileId = coordsToId(coords.q, coords.r);

    tiles[tileId] = {
      q: coords.q,
      r: coords.r,
      type: tileType,
      icon: getEncounterTileIcon(tileType),
      discovered: false,
      visited: false,
    };

    // Place enemies on combat tiles
    if (tileType === ENCOUNTER_TILE_TYPES.COMBAT) {
      enemies[tileId] = generateEncounterEnemy(mobPool, playerLevel, rng);
    }
  }

  return {
    isEncounter: true,
    seed,
    tiles,
    enemies,
    playerPosition: entranceId,
    completed: false,
    playerLevel,
  };
}

/**
 * Select a random encounter tile type based on weights
 */
function selectEncounterTileType(rng, weights) {
  const totalWeight = Object.values(weights).reduce((sum, w) => sum + w, 0);
  const roll = rng() * totalWeight;

  let cumulative = 0;
  for (const [type, weight] of Object.entries(weights)) {
    cumulative += weight;
    if (roll < cumulative) return type;
  }

  return ENCOUNTER_TILE_TYPES.EMPTY;
}

/**
 * Get icon for encounter tile type
 */
function getEncounterTileIcon(type) {
  const icons = {
    [ENCOUNTER_TILE_TYPES.EMPTY]: '·',
    [ENCOUNTER_TILE_TYPES.COMBAT]: '⚔️',
    [ENCOUNTER_TILE_TYPES.REST]: '🔥',
    [ENCOUNTER_TILE_TYPES.ENTRANCE]: '🚪',
    [ENCOUNTER_TILE_TYPES.EXIT]: '🚪',
  };
  return icons[type] || '·';
}

/**
 * Generate an enemy for an encounter tile
 * Scales to player level (no tier scaling like dungeons)
 * @param {Array<string>} mobPool - Available mob IDs
 * @param {number} playerLevel - Player's level for scaling
 * @param {Function} rng - Seeded random function
 * @returns {Object} Enemy data
 */
function generateEncounterEnemy(mobPool, playerLevel, rng) {
  // Select random mob from pool
  let template;
  if (mobPool.length > 0) {
    const mobId = mobPool[Math.floor(rng() * mobPool.length)];
    template = ENEMY_TEMPLATES[mobId];
  }

  // Fallback to goblin if no valid template
  if (!template) {
    template = ENEMY_TEMPLATES['goblin'] || {
      id: 'goblin',
      name: 'Goblin',
      icon: '👺',
      baseStats: { health: 30, attack: 5, defense: 2, speed: 3, range: 1 },
      drops: [],
    };
  }

  // Scale enemy to player level
  // Base stats are designed for level 5, so scale from there
  // Base difficulty of 1.5x makes encounters still challenging
  const BASE_DIFFICULTY = 1.5;
  const levelScaling = Math.max(0.5, playerLevel / 5) * BASE_DIFFICULTY;

  // Apply random variation (85%-115% of scaled stats)
  const variation = 0.85 + rng() * 0.3;
  const finalScaling = levelScaling * variation;

  // Apply stat multipliers (same as dungeon enemies but slightly lower)
  const STAT_MULTIPLIERS = {
    health: 1.4,   // +40% HP
    attack: 1.3,   // +30% attack
    defense: 1.2,  // +20% defense
  };

  const scaledHealth = Math.floor((template.baseStats?.health || 30) * finalScaling * STAT_MULTIPLIERS.health);
  const stats = {
    health: scaledHealth,
    maxHealth: scaledHealth,  // Required for HP bar percentage calculation
    attack: Math.floor((template.baseStats?.attack || 5) * finalScaling * STAT_MULTIPLIERS.attack),
    defense: Math.floor((template.baseStats?.defense || 2) * finalScaling * STAT_MULTIPLIERS.defense),
    speed: template.baseStats?.speed || 3,
    range: template.baseStats?.range || 1,
  };

  // Encounters have reduced drops:
  // - Mob material drops at 50% of normal chance
  // - Very low chance (5%) for equipment drops
  const encounterDrops = (template.drops || []).map(drop => ({
    ...drop,
    // Halve drop chances for encounter mobs (they're easier)
    chance: drop.chance * 0.5,
  }));

  return {
    id: template.id,
    name: template.name,
    icon: template.icon,
    level: playerLevel,
    stats,
    currentHealth: stats.health,
    isAlive: true,
    // Use template's loot table for potential equipment drops (but reduced)
    lootTable: template.lootTable,
    // Reduced material drops for encounters
    drops: encounterDrops,
    // Flag for very low equipment drop rate
    isEncounterMob: true,
  };
}
